import { DiscountUpdateEvent, ThemeEvents } from '@theme/events';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

const MONEY_PLACEHOLDER = '__MONEY__';
const PERCENT_PLACEHOLDER = '__PERCENT__';
const DEFAULT_TIER_QTYS = [5, 10, 20];

class ZentusDiscountLadder extends HTMLElement {
  #abortController = new AbortController();

  #productForm;

  #quantityInput;

  #quantitySelector;

  #summary;

  #youSaveLine;

  #totalLine;

  #cards = [];

  #config;

  #unitPriceMinor = 0;

  #discountSyncTimeout;

  #lastRequestedDiscountCode = null;

  connectedCallback() {
    this.#summary = this.querySelector('[data-summary]');
    this.#youSaveLine = this.querySelector('[data-you-save-line]');
    this.#totalLine = this.querySelector('[data-total-line]');
    this.#cards = Array.from(this.querySelectorAll('[data-tier-card]'));
    this.#refreshFormRefs();

    if (!this.#quantityInput || this.#cards.length === 0) {
      this.hidden = true;
      return;
    }

    this.#ensureInitialQuantity();
    this.#config = this.#buildConfig();

    if (!this.#config.enabled) {
      this.hidden = true;
      return;
    }

    this.#unitPriceMinor = this.#readInitialVariantPrice();
    this.#bindEvents();
    this.#render();
  }

  disconnectedCallback() {
    if (this.#discountSyncTimeout) {
      clearTimeout(this.#discountSyncTimeout);
      this.#discountSyncTimeout = undefined;
    }
    this.#abortController.abort();
  }

  #refreshFormRefs() {
    this.#productForm = this.#resolveProductForm();
    this.#quantityInput = /** @type {HTMLInputElement | null} */ (this.#productForm?.querySelector('input[name="quantity"]'));
    this.#quantitySelector = this.#productForm?.querySelector('quantity-selector-component');
  }

  #resolveProductForm() {
    const adjacentElements = [this.nextElementSibling, this.previousElementSibling].filter(Boolean);

    for (const element of adjacentElements) {
      const directForm =
        /** @type {HTMLElement | null} */ (element.matches?.('product-form-component') ? element : null) ||
        /** @type {HTMLElement | null} */ (element.querySelector?.('product-form-component'));
      if (directForm) return directForm;
    }

    const sectionRoot = this.closest('.product-details, .shopify-section, featured-product-information, main') || document;
    const productId = this.dataset.productId;

    if (productId) {
      const selector = `product-form-component[data-product-id="${productId}"]`;
      const scopedCandidates = Array.from(sectionRoot.querySelectorAll(selector));
      const globalCandidates =
        scopedCandidates.length > 0 ? scopedCandidates : Array.from(document.querySelectorAll(selector));
      if (globalCandidates.length === 1) return globalCandidates[0];
      if (globalCandidates.length > 1) {
        const followingCandidate = globalCandidates.find(
          (candidate) => (this.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
        );
        return followingCandidate || globalCandidates[0];
      }
    }

    return (
      /** @type {HTMLElement | null} */ (this.closest('product-form-component')) ||
      /** @type {HTMLElement | null} */ (sectionRoot.querySelector('product-form-component'))
    );
  }

  #ensureInitialQuantity() {
    if (!this.#quantityInput) return;

    const min = Math.max(1, Number(this.#quantityInput.dataset.min || this.#quantityInput.min || 1));
    const step = Math.max(1, Number(this.#quantityInput.step || 1));
    const max = this.#readOptionalNumberAttribute(this.#quantityInput, 'max');
    let quantity = Number(this.#quantityInput.value);

    if (!Number.isFinite(quantity) || quantity < min) {
      quantity = min;
    }

    if ((quantity - min) % step !== 0) {
      quantity = min;
    }

    if (max !== null) {
      quantity = Math.min(quantity, max);
    }

    if (this.#quantitySelector?.setValue) {
      this.#quantitySelector.setValue(String(quantity));
      this.#quantitySelector.updateButtonStates?.();
    } else {
      this.#quantityInput.value = String(quantity);
    }
  }

  #bindEvents() {
    const { signal } = this.#abortController;

    this.#cards.forEach((card) => {
      card.addEventListener(
        'click',
        () => {
          const tierIndex = Number(card.getAttribute('data-tier-index') || '-1');
          if (Number.isNaN(tierIndex) || tierIndex < 0 || tierIndex > 2) return;
          this.#handleTierClick(tierIndex);
        },
        { signal }
      );
    });

    const sectionTarget = this.closest('.shopify-section, dialog, product-card') || document;

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      (event) => {
        if (!(event instanceof Event) || !(event.target instanceof Node)) return;
        if (!this.#productForm) this.#refreshFormRefs();
        if (!this.#productForm?.contains(event.target)) return;

        const qty = this.#currentQuantity();
        this.#updateSelectedTier(qty);
        this.#updateSummary(qty);
        this.#scheduleDiscountCodeSync(qty);
      },
      { signal }
    );

    sectionTarget.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => {
        if (!event?.detail?.data) return;

        if (event.detail.data.newProduct?.id) {
          this.dataset.productId = event.detail.data.newProduct.id;
        } else if (event.detail.data.productId !== this.dataset.productId) {
          return;
        }

        this.#refreshFormRefs();
        this.#unitPriceMinor = Number(event.detail.resource?.price || 0);

        queueMicrotask(() => {
          const qty = this.#currentQuantity();
          this.#updateCardContent();
          this.#updateSelectedTier(qty);
          this.#updateSummary(qty);
          this.#scheduleDiscountCodeSync(qty);
        });
      },
      { signal }
    );
  }

  #readInitialVariantPrice() {
    const price = Number(this.dataset.initialVariantPrice || 0);
    return Number.isFinite(price) ? price : 0;
  }

  #parseJsonAttribute(attributeName, fallbackValue) {
    const value = this.dataset[attributeName];
    if (!value) return fallbackValue;

    try {
      return JSON.parse(value);
    } catch (_error) {
      return fallbackValue;
    }
  }

  #buildConfig() {
    const globalConfig = this.#parseJsonAttribute('globalConfig', {});
    const productConfig = this.#parseJsonAttribute('productConfig', null);

    const normalizedGlobal = this.#normalizeConfig(globalConfig);
    if (!productConfig || typeof productConfig !== 'object') return normalizedGlobal;

    return this.#mergeProductConfig(normalizedGlobal, productConfig);
  }

  #normalizeConfig(config) {
    const tiers = Array.isArray(config?.tiers) ? config.tiers.slice(0, 3) : [];

    while (tiers.length < 3) {
      tiers.push({ min_qty: DEFAULT_TIER_QTYS[tiers.length] });
    }

    const normalizedTiers = tiers
      .map((tier, index) => ({
        min_qty: Math.max(1, Number(tier?.min_qty || DEFAULT_TIER_QTYS[index])),
        badge: null,
      }))
      .sort((a, b) => a.min_qty - b.min_qty);

    const recommendedIndex = this.#tierTokenToIndex(config?.recommended_tier);
    const maxSavingsIndex = this.#tierTokenToIndex(config?.max_savings_tier);

    if (recommendedIndex !== null) {
      normalizedTiers[recommendedIndex].badge = 'recommended';
    }
    if (maxSavingsIndex !== null) {
      normalizedTiers[maxSavingsIndex].badge = 'max_savings';
    }

    return {
      enabled: Boolean(config?.enabled),
      mode: config?.mode === 'display_only' ? 'display_only' : 'enforced',
      show_you_save: config?.show_you_save !== false,
      show_total: Boolean(config?.show_total),
      auto_apply_discount_codes: Boolean(config?.auto_apply_discount_codes),
      tiers: normalizedTiers,
      price_bands: this.#normalizeBands(config?.price_bands),
      discount_codes: this.#normalizeDiscountCodeMap(config?.discount_codes),
    };
  }

  #mergeProductConfig(globalConfig, productConfig) {
    const merged = this.#deepClone(globalConfig);

    if (typeof productConfig.enabled === 'boolean') {
      merged.enabled = productConfig.enabled;
    }
    if (typeof productConfig.show_you_save === 'boolean') {
      merged.show_you_save = productConfig.show_you_save;
    }
    if (typeof productConfig.show_total === 'boolean') {
      merged.show_total = productConfig.show_total;
    }
    if (typeof productConfig.auto_apply_discount_codes === 'boolean') {
      merged.auto_apply_discount_codes = productConfig.auto_apply_discount_codes;
    }
    if (productConfig.mode === 'display_only' || productConfig.mode === 'enforced') {
      merged.mode = productConfig.mode;
    }

    if (Array.isArray(productConfig.tiers) && productConfig.tiers.length > 0) {
      const productTiers = productConfig.tiers.slice(0, 3);
      while (productTiers.length < 3) {
        productTiers.push({});
      }

      productTiers.forEach((tier, index) => {
        if (Number.isFinite(Number(tier?.min_qty))) {
          merged.tiers[index].min_qty = Math.max(1, Number(tier.min_qty));
        }
      });

      merged.tiers.sort((a, b) => a.min_qty - b.min_qty);

      if (Number.isFinite(Number(productConfig.recommended_min_qty))) {
        const recommendedTier = merged.tiers.find((tier) => tier.min_qty === Number(productConfig.recommended_min_qty));
        if (recommendedTier) {
          merged.tiers.forEach((tier) => {
            if (tier.badge === 'recommended') tier.badge = null;
          });
          recommendedTier.badge = 'recommended';
        }
      }

      if (Number.isFinite(Number(productConfig.max_savings_min_qty))) {
        const maxSavingsTier = merged.tiers.find((tier) => tier.min_qty === Number(productConfig.max_savings_min_qty));
        if (maxSavingsTier) {
          merged.tiers.forEach((tier) => {
            if (tier.badge === 'max_savings') tier.badge = null;
          });
          maxSavingsTier.badge = 'max_savings';
        }
      }

      this.#mergeTierBadgesFromProductConfig(merged.tiers, productTiers);
      this.#mergeTierDiscountsObject(productConfig, merged.price_bands);
    }

    if (Array.isArray(productConfig.price_bands) && productConfig.price_bands.length > 0) {
      merged.price_bands = this.#normalizeBands(productConfig.price_bands, merged.price_bands);
    }

    if (productConfig.discount_codes && typeof productConfig.discount_codes === 'object') {
      merged.discount_codes = this.#normalizeDiscountCodeMap(productConfig.discount_codes, merged.discount_codes);
    }

    return merged;
  }

  #mergeTierBadgesFromProductConfig(tiers, productTiers) {
    productTiers.forEach((tier, index) => {
      if (tier?.badge !== 'recommended' && tier?.badge !== 'max_savings') return;

      tiers.forEach((targetTier) => {
        if (targetTier.badge === tier.badge) {
          targetTier.badge = null;
        }
      });

      let targetTier = null;
      if (Number.isFinite(Number(tier?.min_qty))) {
        targetTier = tiers.find((candidateTier) => candidateTier.min_qty === Number(tier.min_qty)) || null;
      }

      if (!targetTier && tiers[index]) {
        targetTier = tiers[index];
      }

      if (targetTier) {
        targetTier.badge = tier.badge;
      }
    });
  }

  #mergeTierDiscountsObject(productConfig, priceBands) {
    if (!Array.isArray(productConfig?.tiers)) return;

    const bandKeyToIndex = { A: 0, B: 1, C: 2 };

    productConfig.tiers.forEach((tierConfig, tierIndex) => {
      if (!tierConfig?.discounts || typeof tierConfig.discounts !== 'object') return;

      Object.entries(bandKeyToIndex).forEach(([key, bandIndex]) => {
        const nextValue = Number(tierConfig.discounts[key]);
        if (!Number.isFinite(nextValue) || !priceBands[bandIndex]) return;

        priceBands[bandIndex].discounts[tierIndex] = this.#sanitizeDiscount(nextValue);
      });
    });
  }

  #normalizeDiscountCodeMap(discountCodes, fallbackMap) {
    const baseMap =
      fallbackMap && typeof fallbackMap === 'object'
        ? this.#deepClone(fallbackMap)
        : {
            A: ['', '', ''],
            B: ['', '', ''],
            C: ['', '', ''],
          };

    if (!discountCodes || typeof discountCodes !== 'object') {
      return baseMap;
    }

    ['A', 'B', 'C'].forEach((bandKey) => {
      const candidate = Array.isArray(discountCodes[bandKey]) ? discountCodes[bandKey] : baseMap[bandKey];
      baseMap[bandKey] = [0, 1, 2].map((tierIndex) => this.#normalizeDiscountCodeValue(candidate?.[tierIndex]));
    });

    return baseMap;
  }

  #normalizeDiscountCodeValue(value) {
    if (typeof value !== 'string') return '';
    return value.trim();
  }

  #normalizeBands(bands, fallbackBands) {
    const baseBands = Array.isArray(fallbackBands)
      ? this.#deepClone(fallbackBands)
      : [
          { key: 'A', min_price: 0, max_price: 15, discounts: [5, 10, 15] },
          { key: 'B', min_price: 15, max_price: 60, discounts: [4, 8, 12] },
          { key: 'C', min_price: 60, max_price: null, discounts: [3, 6, 9] },
        ];

    if (!Array.isArray(bands) || bands.length === 0) {
      return baseBands;
    }

    const normalized = baseBands.map((band, index) => {
      const candidate = bands[index] || {};
      const nextDiscounts = Array.isArray(candidate.discounts) ? candidate.discounts : band.discounts;

      return {
        key: typeof candidate.key === 'string' ? candidate.key.toUpperCase() : band.key,
        min_price: Number.isFinite(Number(candidate.min_price)) ? Number(candidate.min_price) : band.min_price,
        max_price:
          candidate.max_price === null
            ? null
            : Number.isFinite(Number(candidate.max_price))
            ? Number(candidate.max_price)
            : band.max_price,
        discounts: [0, 1, 2].map((discountIndex) =>
          this.#sanitizeDiscount(Number(nextDiscounts[discountIndex] ?? band.discounts[discountIndex]))
        ),
      };
    });

    return normalized.sort((a, b) => a.min_price - b.min_price);
  }

  #sanitizeDiscount(value) {
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(100, value));
  }

  #tierTokenToIndex(token) {
    if (token === 'tier_1') return 0;
    if (token === 'tier_2') return 1;
    if (token === 'tier_3') return 2;
    return null;
  }

  #render() {
    this.#updateCardContent();

    const quantity = this.#currentQuantity();
    this.#updateSelectedTier(quantity);
    this.#updateSummary(quantity);
    this.#scheduleDiscountCodeSync(quantity);
  }

  #currentQuantity() {
    if (!this.#quantityInput) this.#refreshFormRefs();
    return Math.max(1, Number(this.#quantityInput?.value || 1));
  }

  #handleTierClick(tierIndex) {
    const activeTier = this.#resolveTierIndexForQuantity(this.#currentQuantity());
    const selectedTier = this.#config.tiers[tierIndex];
    if (!selectedTier) return;

    if (activeTier === tierIndex) {
      const fallbackQty = Number(this.#quantityInput?.dataset.min || this.#quantityInput?.min || 1);
      this.#setQuantity(fallbackQty);
      return;
    }

    this.#setQuantity(selectedTier.min_qty);
  }

  #setQuantity(nextQuantity) {
    if (!this.#quantityInput) this.#refreshFormRefs();
    if (!this.#quantityInput) return;

    const snappedQuantity = this.#snapQuantityToRules(nextQuantity);
    const snappedQuantityString = snappedQuantity.toString();

    if (this.#quantitySelector?.setValue) {
      this.#quantitySelector.setValue(snappedQuantityString);
      this.#quantitySelector.updateButtonStates?.();
      if (typeof this.#quantitySelector.onQuantityChange === 'function') {
        this.#quantitySelector.onQuantityChange();
      }
    } else {
      this.#quantityInput.value = snappedQuantityString;
    }

    // Keep the native form input in sync for add-to-cart form submission and downstream listeners.
    this.#quantityInput.value = snappedQuantityString;
    this.#quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.#quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

    this.#updateSelectedTier(snappedQuantity);
    this.#updateSummary(snappedQuantity);
    this.#scheduleDiscountCodeSync(snappedQuantity);
  }

  #snapQuantityToRules(nextQuantity) {
    if (!this.#quantityInput) return 1;

    const min = Math.max(1, Number(this.#quantityInput.min || this.#quantityInput.dataset.min || 1));
    const step = Math.max(1, Number(this.#quantityInput.step || 1));
    let quantity = Math.max(min, Math.round(Number(nextQuantity) || min));

    if ((quantity - min) % step !== 0) {
      quantity = min + Math.ceil((quantity - min) / step) * step;
    }

    const maxFromInput = this.#readOptionalNumberAttribute(this.#quantityInput, 'max');
    let maxFromComponent = null;
    if (typeof this.#quantitySelector?.getEffectiveMax === 'function') {
      const rawMax = this.#quantitySelector.getEffectiveMax();
      if (typeof rawMax === 'number' && Number.isFinite(rawMax)) {
        maxFromComponent = rawMax;
      }
    }

    let effectiveMax = maxFromInput;
    if (maxFromComponent !== null) {
      effectiveMax = effectiveMax === null ? maxFromComponent : Math.min(effectiveMax, maxFromComponent);
    }

    if (Number.isFinite(effectiveMax)) {
      quantity = Math.min(quantity, Number(effectiveMax));
    }

    return quantity;
  }

  #updateCardContent() {
    const piecesLabel = this.dataset.piecesLabel || '';
    const activeBand = this.#resolveBandForCurrentVariant();

    this.#cards.forEach((card, index) => {
      const tier = this.#config.tiers[index];
      if (!tier) return;

      const quantityEl = card.querySelector('[data-tier-quantity]');
      if (quantityEl) {
        quantityEl.textContent = `${tier.min_qty}+ ${piecesLabel}`;
      }

      const saveEl = card.querySelector('[data-tier-save]');
      if (saveEl) {
        const tierDiscount = activeBand?.discounts[index] ?? 0;
        saveEl.textContent = this.#applyTemplate(this.dataset.saveTemplate, PERCENT_PLACEHOLDER, `${tierDiscount}%`);
      }

      const badgeEl = card.querySelector('[data-tier-badge]');
      if (badgeEl) {
        const badgeLabel = this.#badgeLabelForTier(tier.badge);
        if (badgeLabel) {
          badgeEl.textContent = badgeLabel;
          badgeEl.hidden = false;
        } else {
          badgeEl.hidden = true;
          badgeEl.textContent = '';
        }
      }
    });
  }

  #updateSelectedTier(quantity) {
    const activeTier = this.#resolveTierIndexForQuantity(quantity);

    this.#cards.forEach((card, index) => {
      const isSelected = activeTier === index;
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  #updateSummary(quantity) {
    if (!this.#summary || !this.#youSaveLine || !this.#totalLine) return;

    const activeTierIndex = this.#resolveTierIndexForQuantity(quantity);
    const activeBand = this.#resolveBandForCurrentVariant();
    const discountPercent = activeTierIndex === null ? 0 : activeBand?.discounts[activeTierIndex] ?? 0;

    const rawTotal = Math.max(0, Math.round(this.#unitPriceMinor * quantity));
    const savingsMinor = Math.round(rawTotal * (discountPercent / 100));
    const discountedTotalMinor = Math.max(0, rawTotal - savingsMinor);
    const hasSavings = savingsMinor > 0;
    const shouldShowYouSave = this.#config.show_you_save && hasSavings;
    const shouldShowTotal = this.#config.show_total;

    const formattedSavings = this.#formatMoney(savingsMinor);
    const formattedTotal = this.#formatMoney(discountedTotalMinor);

    this.#setTextWithHighlightedValue(
      this.#youSaveLine,
      this.dataset.youSaveTemplate,
      MONEY_PLACEHOLDER,
      formattedSavings
    );
    this.#totalLine.textContent = this.#applyTemplate(this.dataset.totalTemplate, MONEY_PLACEHOLDER, formattedTotal);

    this.#youSaveLine.classList.toggle('is-hidden', !shouldShowYouSave);
    this.#totalLine.classList.toggle('is-hidden', !shouldShowTotal);
    this.#summary.classList.toggle('is-hidden', !shouldShowYouSave && !shouldShowTotal);
  }

  #scheduleDiscountCodeSync(quantity) {
    if (this.#config.mode !== 'enforced') return;
    if (!this.#config.auto_apply_discount_codes) return;

    if (this.#discountSyncTimeout) {
      clearTimeout(this.#discountSyncTimeout);
    }

    this.#discountSyncTimeout = setTimeout(() => {
      this.#discountSyncTimeout = undefined;
      this.#syncDiscountCodeForQuantity(quantity);
    }, 180);
  }

  async #syncDiscountCodeForQuantity(quantity) {
    const desiredCode = this.#resolveDiscountCodeForQuantity(quantity);
    const ladderCodes = this.#allConfiguredLadderCodes();

    if (ladderCodes.size === 0) return;
    if (desiredCode === this.#lastRequestedDiscountCode) return;

    this.#lastRequestedDiscountCode = desiredCode;

    try {
      const cartResponse = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      const existingCodes = (Array.isArray(cart?.discount_codes) ? cart.discount_codes : [])
        .map((discountCode) => {
          if (typeof discountCode === 'string') return discountCode;
          if (discountCode && typeof discountCode.code === 'string') return discountCode.code;
          return '';
        })
        .map((discountCode) => discountCode.trim())
        .filter(Boolean);

      const nextCodes = existingCodes.filter((discountCode) => !ladderCodes.has(discountCode));
      if (desiredCode) {
        nextCodes.push(desiredCode);
      }

      if (nextCodes.join(',') === existingCodes.join(',')) return;

      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ discount: nextCodes.join(',') }),
      });

      if (!updateResponse.ok) return;
      const payload = await updateResponse.json();
      document.dispatchEvent(new DiscountUpdateEvent(payload, this.id || 'zentus-discount-ladder'));
    } catch (_error) {
      // Ignore network/auth errors (for example local proxy preview restrictions).
    }
  }

  #resolveDiscountCodeForQuantity(quantity) {
    const activeTierIndex = this.#resolveTierIndexForQuantity(quantity);
    if (activeTierIndex === null) return '';

    const activeBand = this.#resolveBandForCurrentVariant();
    const bandKey = typeof activeBand?.key === 'string' ? activeBand.key : 'A';
    const bandCodes = this.#config.discount_codes?.[bandKey];
    if (!Array.isArray(bandCodes)) return '';

    return this.#normalizeDiscountCodeValue(bandCodes[activeTierIndex]);
  }

  #allConfiguredLadderCodes() {
    const allCodes = new Set();

    ['A', 'B', 'C'].forEach((bandKey) => {
      const bandCodes = this.#config.discount_codes?.[bandKey];
      if (!Array.isArray(bandCodes)) return;

      bandCodes.forEach((code) => {
        const normalized = this.#normalizeDiscountCodeValue(code);
        if (normalized) {
          allCodes.add(normalized);
        }
      });
    });

    return allCodes;
  }

  #resolveTierIndexForQuantity(quantity) {
    let activeIndex = null;

    this.#config.tiers.forEach((tier, index) => {
      if (quantity >= tier.min_qty) {
        activeIndex = index;
      }
    });

    return activeIndex;
  }

  #resolveBandForCurrentVariant() {
    const baseMinor = this.#convertActiveMoneyToBaseMinor(this.#unitPriceMinor);

    return (
      this.#config.price_bands.find((band) => {
        const bandMin = this.#baseMajorToMinor(band.min_price);
        const bandMax = band.max_price === null ? null : this.#baseMajorToMinor(band.max_price);

        if (baseMinor < bandMin) return false;
        if (bandMax !== null && baseMinor >= bandMax) return false;
        return true;
      }) || this.#config.price_bands[this.#config.price_bands.length - 1]
    );
  }

  #convertActiveMoneyToBaseMinor(amountMinor) {
    const rate = Number(window.Shopify?.currency?.rate || 1);

    if (!Number.isFinite(rate) || rate <= 0) {
      return amountMinor;
    }

    return Math.round(amountMinor / rate);
  }

  #baseMajorToMinor(amountMajor) {
    const baseCurrency = this.dataset.shopCurrency || 'USD';
    const converted = convertMoneyToMinorUnits(String(amountMajor), baseCurrency);
    if (converted === null) {
      return Math.round(Number(amountMajor || 0) * 100);
    }

    return converted;
  }

  #formatMoney(amountMinor) {
    const useCurrencyCode = this.dataset.currencyCodeEnabled === 'true';
    const format = useCurrencyCode ? this.dataset.moneyWithCurrencyFormat : this.dataset.moneyFormat;
    const activeCurrency = window.Shopify?.currency?.active || this.dataset.activeCurrency || this.dataset.shopCurrency || 'USD';

    if (!format) {
      return amountMinor.toString();
    }

    return formatMoney(amountMinor, format, activeCurrency);
  }

  #badgeLabelForTier(badgeType) {
    if (badgeType === 'recommended') return this.dataset.recommendedLabel || '';
    if (badgeType === 'max_savings') return this.dataset.maxSavingsLabel || '';
    return '';
  }

  #applyTemplate(template, placeholder, replacement) {
    if (!template) return replacement;
    return template.replace(placeholder, replacement);
  }

  #setTextWithHighlightedValue(target, template, placeholder, highlightedValue) {
    if (!target) return;

    const safeTemplate = template || placeholder;
    const segments = safeTemplate.split(placeholder);
    if (segments.length === 1) {
      target.textContent = this.#applyTemplate(template, placeholder, highlightedValue);
      return;
    }

    target.replaceChildren();
    segments.forEach((segment, index) => {
      if (segment) {
        target.append(document.createTextNode(segment));
      }

      if (index < segments.length - 1) {
        const valueElement = document.createElement('strong');
        valueElement.className = 'zentus-discount-ladder__value';
        valueElement.textContent = highlightedValue;
        target.append(valueElement);
      }
    });
  }

  #deepClone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  #readOptionalNumberAttribute(element, attributeName) {
    const raw = element.getAttribute(attributeName);
    if (raw == null || raw === '') return null;

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
}

if (!customElements.get('zentus-discount-ladder')) {
  customElements.define('zentus-discount-ladder', ZentusDiscountLadder);
}
